import * as THREE from 'three';

/**
 * Renderer类 - 封装Three.js渲染逻辑
 * 
 * 职责:
 * - 创建和管理Three.js Scene、Camera、WebGLRenderer
 * - 设置背景渐变效果（深蓝到黑色）
 * - 配置抗锯齿和渲染参数
 * - 根据设备能力调整渲染质量
 * 
 * 验证需求: Requirements 1.1, 1.2, 1.4, 1.5
 */
class Renderer {
  constructor(container) {
    if (!container) {
      throw new Error('Container element is required');
    }
    
    this.container = container;
    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.backgroundMesh = null;
  }

  /**
   * 初始化渲染器、场景和相机
   * @throws {Error} 如果WebGL不支持
   */
  init() {
    try {
      // 创建WebGLRenderer，启用抗锯齿和alpha通道
      this.renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: true
      });
      
      // 根据设备能力设置像素比率
      // 移动设备降低pixelRatio以提升性能
      const isMobile = this._isMobileDevice();
      const pixelRatio = isMobile ? Math.min(window.devicePixelRatio, 2) : window.devicePixelRatio;
      this.renderer.setPixelRatio(pixelRatio);
      
      // 设置初始尺寸
      this.renderer.setSize(this.container.clientWidth, this.container.clientHeight);
      
      // 将canvas添加到容器
      this.container.appendChild(this.renderer.domElement);
      
    } catch (error) {
      throw new Error(`WebGL initialization failed: ${error.message}`);
    }

    // 创建场景
    this.scene = new THREE.Scene();
    
    // 创建透视相机
    const aspect = this.container.clientWidth / this.container.clientHeight;
    this.camera = new THREE.PerspectiveCamera(
      60,    // FOV
      aspect, // 宽高比
      0.1,   // near
      1000   // far
    );
    
    // 设置相机初始位置
    this.camera.position.set(0, 10, 50);
    this.camera.lookAt(0, 0, 0);
    
    // 创建背景渐变效果（深蓝到黑色）
    this._createGradientBackground();
  }

  /**
   * 创建背景渐变效果
   * 使用ShaderMaterial实现科技感的深蓝渐变背景
   */
  _createGradientBackground() {
    // 创建全屏平面几何体
    const geometry = new THREE.PlaneGeometry(2, 2);
    
    // 自定义着色器实现科技感渐变
    const material = new THREE.ShaderMaterial({
      uniforms: {
        time: { value: 0 }
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform float time;
        varying vec2 vUv;
        
        void main() {
          // 深蓝色科技感渐变
          vec3 color1 = vec3(0.0, 0.05, 0.15);  // 深蓝黑
          vec3 color2 = vec3(0.0, 0.12, 0.24);  // 中蓝
          vec3 color3 = vec3(0.0, 0.08, 0.18);  // 深蓝
          
          // 创建多层渐变
          float gradient1 = smoothstep(0.0, 0.5, vUv.y);
          float gradient2 = smoothstep(0.5, 1.0, vUv.y);
          
          // 添加径向渐变效果
          vec2 center = vec2(0.5, 0.3);
          float dist = distance(vUv, center);
          float radial = smoothstep(0.8, 0.0, dist);
          
          // 混合颜色
          vec3 color = mix(color1, color2, gradient1);
          color = mix(color, color3, gradient2);
          color += color2 * radial * 0.3;
          
          gl_FragColor = vec4(color, 1.0);
        }
      `,
      depthWrite: false,
      depthTest: false
    });
    
    this.backgroundMesh = new THREE.Mesh(geometry, material);
    this.backgroundMesh.renderOrder = -1; // 确保背景最先渲染
    
    // 使用正交相机渲染背景
    this.backgroundMesh.frustumCulled = false;
    this.scene.add(this.backgroundMesh);
  }

  /**
   * 检测是否为移动设备
   * @returns {boolean}
   */
  _isMobileDevice() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  /**
   * 设置渲染器尺寸
   * @param {number} width - 宽度
   * @param {number} height - 高度
   */
  setSize(width, height) {
    if (!this.renderer || !this.camera) {
      console.warn('Renderer not initialized');
      return;
    }
    
    this.renderer.setSize(width, height);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  /**
   * 渲染场景
   */
  render() {
    if (!this.renderer || !this.scene || !this.camera) {
      console.warn('Renderer not fully initialized');
      return;
    }
    
    this.renderer.render(this.scene, this.camera);
  }

  /**
   * 获取场景对象
   * @returns {THREE.Scene}
   */
  getScene() {
    return this.scene;
  }

  /**
   * 获取相机对象
   * @returns {THREE.PerspectiveCamera}
   */
  getCamera() {
    return this.camera;
  }

  /**
   * 添加对象到场景
   * @param {THREE.Object3D} object - 要添加的对象
   */
  addToScene(object) {
    if (!this.scene) {
      console.warn('Scene not initialized');
      return;
    }
    this.scene.add(object);
  }

  /**
   * 从场景移除对象
   * @param {THREE.Object3D} object - 要移除的对象
   */
  removeFromScene(object) {
    if (!this.scene) {
      console.warn('Scene not initialized');
      return;
    }
    this.scene.remove(object);
  }

  /**
   * 清理资源
   */
  dispose() {
    if (this.backgroundMesh) {
      this.backgroundMesh.geometry.dispose();
      this.backgroundMesh.material.dispose();
      this.scene.remove(this.backgroundMesh);
    }
    
    if (this.renderer) {
      this.renderer.dispose();
      if (this.renderer.domElement && this.renderer.domElement.parentNode) {
        this.renderer.domElement.parentNode.removeChild(this.renderer.domElement);
      }
    }
  }
}

export default Renderer;
